/**
 * 26. Payments — list; payment runs; bank account; status;
 * reconciliation.
 *
 * PAY-ALLOC (payment-allocation.md §4.1): the Unallocated column shows what a
 * posted payment still holds on account (an overpayment, an advance, cash not
 * yet applied), so the remainder is on screen, never hidden.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface PaymentRow {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  paymentType: "Incoming" | "Outgoing";
  partyName: string;
  bankAccountName: string;
  status: "Draft" | "Approved" | "Posted" | "Reconciled" | "Reversed";
  amount: string;
  /** The API's unallocated_amount (camelCase alias): today's remainder. */
  unallocatedAmount?: string;
  currencyCode: string;
}

/** A posted payment holding something on account. */
function holdsOnAccount(r: PaymentRow): boolean {
  return (
    (r.status === "Posted" || r.status === "Reconciled") &&
    typeof r.unallocatedAmount === "string" &&
    !/^0+(\.0+)?$/.test(r.unallocatedAmount)
  );
}

export function PaymentsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<PaymentRow>(
    ["finance", "payments"],
    "/finance/payments",
  );

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Incoming and outgoing payments, runs, and reconciliation."
        actions={
          <PendingButton variant="primary" reason="paymentEditor" slice="ED-W5">
            {t("documentActions.newPayment")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Payments (${data.total})`}>
          <DataTable<PaymentRow>
            rows={data.items}
            columns={[
              {
                key: "paymentNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.paymentNumber}</span>
                ),
              },
              {
                key: "paymentDate",
                label: "Date",
                render: (r) => formatDate(r.paymentDate),
              },
              { key: "paymentType", label: "Type" },
              { key: "partyName", label: "Party" },
              { key: "bankAccountName", label: "Bank account" },
              {
                key: "amount",
                label: "Amount",
                numeric: true,
                render: (r) => formatMoney(r.amount, r.currencyCode),
              },
              {
                key: "unallocatedAmount",
                label: t("fields.unallocated"),
                numeric: true,
                render: (r) =>
                  holdsOnAccount(r)
                    ? formatMoney(r.unallocatedAmount!, r.currencyCode)
                    : "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
